import { Divider, Grid } from '@mui/material';
import { type ReactNode, createContext, useContext, useMemo } from 'react';
import type { Path } from 'react-hook-form';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { ColorPickerWidget } from 'src/components/widget/colorPickerWidget/ColorPickerWidget';
import { NumberInputWidget } from 'src/components/widget/numberInputWidget/NumberInputWidget';
import { SelectDropdownWidget } from 'src/components/widget/selectDropdownWidget/SelectDropdownWidget';
import { FLEX_WRAP_OPTIONS, FONT_FAMILY_OPTIONS } from 'src/constants';
import { useGetProjectConfig } from 'src/hooks';
import type { ISubproductThemeType } from 'src/interfaces';
import { lang } from 'src/utils';

import type { ProductMoleculeItemProps } from './ProductMoleculeItem.interface';

type MappedFontOption = { label: string; value: string };

const ProductMoleculeFontsContext = createContext<MappedFontOption[] | null>(
  null,
);

export const ProductMoleculeFontsProvider = ({
  mappedFonts,
  children,
}: {
  mappedFonts: MappedFontOption[];
  children: ReactNode;
}) => (
  <ProductMoleculeFontsContext.Provider value={mappedFonts}>
    {children}
  </ProductMoleculeFontsContext.Provider>
);

const mapProjectFonts = (
  fonts: { name: string }[] | undefined,
): MappedFontOption[] => {
  const projectFonts =
    fonts?.map((item) => ({
      label: item.name,
      value: item.name,
    })) ?? [];

  return projectFonts.length > 0 ? projectFonts : FONT_FAMILY_OPTIONS;
};

const ProductMoleculeItemBody = ({
  mappedFonts,
  ...props
}: ProductMoleculeItemProps & { mappedFonts: MappedFontOption[] }) => {
  const handleChange =
    (field: Path<ISubproductThemeType['label']>) => (value: unknown) => {
      props.handleChange(value, field);
    };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <InputTextLabel>{props.title}</InputTextLabel>
      </Grid>

      {props.customElement}

      {!props.options?.hideAllElements && (
        <>
          <Grid item xs={6}>
            <SelectDropdownWidget
              selectOptions={mappedFonts}
              label={lang.fontFamily}
              value={props?.value?.font}
              onChange={handleChange('font')}
            />
          </Grid>

          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.fontSize}
              value={props.value?.fontSize}
              onChange={handleChange('fontSize')}
              options={{ suffix: 'px', useImmediateUpdate: true }}
            />
          </Grid>

          <Grid item xs={6}>
            <ColorPickerWidget
              label={lang.fontColor}
              value={props.value?.color}
              onChange={handleChange('color')}
              options={{
                ...(props?.options?.colorPickerPosition !== 'center' && {
                  triangle: 'hide',
                }),
              }}
              colorPickerMode={
                props.options?.showBrandedColorPicker?.includes('color')
                  ? 'block'
                  : 'sketch'
              }
              pickerWrapperStyle={
                props?.options?.colorPickerPosition === 'top'
                  ? { left: '0', top: '-200px' }
                  : props?.options?.colorPickerPosition === 'right'
                  ? { left: '0' }
                  : {}
              }
            />
          </Grid>

          <Grid item xs={6}>
            <NumberInputWidget
              label={lang.lineHeight}
              value={props.value?.lineHeight}
              onChange={handleChange('lineHeight')}
              options={{ suffix: 'px', useImmediateUpdate: true }}
            />
          </Grid>

          {props?.options?.showBgColor && (
            <Grid item xs={6}>
              <ColorPickerWidget
                label={lang.backgroundColor}
                value={props?.value?.backgroundColor}
                onChange={handleChange('backgroundColor')}
                options={{
                  hideAIcon: true,
                  backgroundColor: true,
                  border: true,
                  ...(props.options.colorPickerPosition !== 'center' && {
                    triangle: 'hide',
                  }),
                }}
                colorPickerMode={
                  props.options?.showBrandedColorPicker?.includes(
                    'backgroundColor',
                  )
                    ? 'block'
                    : 'sketch'
                }
                pickerWrapperStyle={
                  props.options.colorPickerPosition === 'top'
                    ? { left: '0', top: '-200px' }
                    : props.options.colorPickerPosition === 'right'
                    ? { left: '0' }
                    : {}
                }
              />
            </Grid>
          )}

          {props?.options?.showHeight && (
            <Grid item xs={6}>
              <NumberInputWidget
                label={lang.height}
                value={props.value?.height}
                onChange={handleChange('height')}
                options={{ useImmediateUpdate: true }}
              />
            </Grid>
          )}

          {props?.options?.showFlexWrap && (
            <Grid item xs={6} data-testid="flex-wrap-select-container">
              <SelectDropdownWidget
                selectOptions={FLEX_WRAP_OPTIONS}
                label={lang.flexWrap}
                data-testid="flex-wrap-select"
                value={props?.value?.flexWrap ?? FLEX_WRAP_OPTIONS[0].value}
                onChange={handleChange('flexWrap')}
              />
            </Grid>
          )}
        </>
      )}

      {props?.options?.showDivider && (
        <Grid item xs={12}>
          <Divider />
        </Grid>
      )}
    </Grid>
  );
};

const SelfFetchingProductMoleculeItem = (props: ProductMoleculeItemProps) => {
  const { data: projectConfig } = useGetProjectConfig();
  const mappedFonts = useMemo(
    () => mapProjectFonts(projectConfig?.fonts),
    [projectConfig?.fonts],
  );

  return <ProductMoleculeItemBody mappedFonts={mappedFonts} {...props} />;
};

export const ProductMoleculeItem = (props: ProductMoleculeItemProps) => {
  const contextFonts = useContext(ProductMoleculeFontsContext);

  if (contextFonts) {
    return <ProductMoleculeItemBody mappedFonts={contextFonts} {...props} />;
  }

  return <SelfFetchingProductMoleculeItem {...props} />;
};
